"use client";
import type {
  ComponentPropsWithoutRef,
  HTMLAttributes,
  ReactNode,
} from "react";
import {
  Controller,
  type FieldPath,
  type FieldValues,
  useFormContext,
} from "react-hook-form";
import { FileUploaderContent } from "./file-uploader-content";

// ----------------------------------------------------------------------

interface CustomRHFFileUploaderProps<TFieldValues extends FieldValues>
  extends Omit<
    ComponentPropsWithoutRef<typeof Controller>,
    "name" | "control" | "render"
  > {
  name: FieldPath<TFieldValues>;
  helperText?: string;
  containerClass?: Pick<HTMLAttributes<HTMLDivElement>, "className">;
  loading?: boolean;
  icon: ReactNode;
  title: string;
  allowedFormats: string[];
  maxSize: number; // in MB
  buttonText: string;
  accept?: string;
  multiple?: boolean;
  required?: boolean;
}

export default function CustomRHFFileUploader<
  TFieldValues extends FieldValues,
>({
  name,
  helperText,
  defaultValue,
  rules,
  containerClass,
  loading,
  icon,
  title,
  allowedFormats,
  maxSize,
  buttonText,
  accept,
  multiple = false,
  required = false,
  ...other
}: CustomRHFFileUploaderProps<TFieldValues>) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      render={({ field, fieldState }) => (
        <FileUploaderContent
          field={field}
          fieldState={fieldState}
          multiple={multiple}
          allowedFormats={allowedFormats}
          maxSize={maxSize}
          accept={accept}
          icon={icon}
          title={title}
          buttonText={buttonText}
          loading={loading}
          required={required}
          helperText={helperText}
          containerClass={containerClass}
        />
      )}
      {...other}
    />
  );
}
